import React from "react";
import { AiOutlineClose } from "react-icons/ai";
import { ButtonComp, TextInputComp, FormikSelectComp } from "../../../../components";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import { useGetOccupationsQuery } from "../../../../store/selectableSlice";

export default function AddNonMemberGuarantorForm({
  Back,
  onSubmit,
  isLoading = false,
}) {
  const { data: occupations = [] } = useGetOccupationsQuery();

  // Format occupations for dropdown and add "Others" option
  const occupationOptions = React.useMemo(() => {
    const formatted = occupations
      .filter((occ) => occ && occ.name)
      .map((occ) => ({
        label: occ.name,
        value: occ.name,
      }));
    
    // Add "Others" option at the end
    formatted.push({ label: "Others", value: "others" });
    
    return formatted;
  }, [occupations]);

  const initialValues = {
    name: "",
    phone: "",
    email: "",
    occupation_id: "",
    custom_occupation: "",
    home_address: "",
    office_address: "",
  };
  
  const validationSchema = Yup.object({
    name: Yup.string().required("Required"),
    phone: Yup.string().required("Required"),
    email: Yup.string().email().required("Required"),
    occupation_id: Yup.string().required("Required"),
    custom_occupation: Yup.string().when("occupation_id", {
      is: "others",
      then: (schema) => schema.required("Please specify occupation"),
      otherwise: (schema) => schema.notRequired(),
    }),
    home_address: Yup.string().required("Required"),
    office_address: Yup.string().required("Required"),
  });
  const handleFormSubmit = async (values, { resetForm, setSubmitting }) => {
    if (onSubmit) {
      try {
        // Determine the final occupation value
        const occupation = values.occupation_id === "others" 
          ? values.custom_occupation 
          : values.occupation_id;
        
        // Prepare the payload with the correct occupation
        // Ensure phone is a string
        const payload = {
          ...values,
          occupation: occupation,
          phone: String(values.phone || ""), // Convert phone to string
        };
        
        // Remove the temporary fields
        delete payload.occupation_id;
        delete payload.custom_occupation;
        
        const result = await onSubmit(payload);
        
        // Only reset form if submission was successful
        if (result === true) {
          resetForm();
        }
        // If result is false, don't reset - keep form data for user to correct
      } catch (error) {
        // Don't reset form on error - let user keep their input
        console.error("Form submission error:", error);
      } finally {
        setSubmitting(false);
      }
    }
  };

  return (
    <section className="w-full px-4">
      <div className="flex mb-4 py-3  justify-center items-center relative border-b ">
        <AiOutlineClose
          size={20}
          onClick={Back}
          className=" pointer absolute top-6 left-4  "
        />
        <h4 className="fw-bold mb-0 text-black1 text-[1.5rem]">
          Non-member Guarantor
        </h4>
      </div>

      <div className="w-full">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
          className="w-full"
        >
          {() => {
            // Component to conditionally show custom occupation input
            const OccupationField = () => {
              const { values, setFieldValue } = useFormikContext();
              const showCustomInput = values.occupation_id === "others";

              // Handle occupation change - clear custom field if not "others"
              const handleOccupationChange = (selectedValue) => {
                // Clear custom occupation if not "others"
                if (selectedValue !== "others") {
                  setFieldValue("custom_occupation", "", false);
                }
              };

              return (
                <>
                  <div className="">
                    <FormikSelectComp
                      fieldName="occupation_id"
                      labelText="Occupation"
                      LabelClassName="fw-3"
                      options={occupationOptions}
                      selectText="Select occupation"
                      isRequired={true}
                      backgroundColor="#fafafa"
                      onValueChange={handleOccupationChange}
                    />
                  </div>
                  {showCustomInput && (
                    <div className="md:col-span-2">
                      <TextInputComp
                        LabelText="Specify Occupation"
                        borderColor="#A1A0A0"
                        LabelClassName="fw-3"
                        placeholder="Enter occupation"
                        name="custom_occupation"
                        type="text"
                      />
                    </div>
                  )}
                </>
              );
            };

            return (
              <Form className="w-full">
                <div className="grid md:grid-cols-2 gap-y-2 gap-x-6">
                  <div className="">
                    <TextInputComp
                      LabelText={"Full name"}
                      borderColor="#A1A0A0"
                      LabelClassName={"fw-3"}
                      placeholder={"Olafare Smith Julius"}
                      name="name"
                      type="text"
                    />
                  </div>
                  <div className="">
                    <TextInputComp
                      LabelText={"Phone number"}
                      borderColor="#A1A0A0"
                      LabelClassName={"fw-3"}
                      placeholder={"Enter phone number"}
                      name="phone"
                      type="text"
                    />
                  </div>
                  <div className="">
                    <TextInputComp
                      LabelText={"Email address"}
                      borderColor="#A1A0A0"
                      LabelClassName={"fw-3"}
                      placeholder={"Enter email address"}
                      name="email"
                      type="text"
                    />
                  </div>
                  <OccupationField />
                <div className="">
                  <TextInputComp
                    LabelText={"Home address"}
                    borderColor="#A1A0A0"
                    LabelClassName={"fw-3"}
                    placeholder={"Enter home address"}
                    name="home_address"
                    type="text"
                  />
                </div>
                <div className="">
                  <TextInputComp
                    LabelText={"Office address"}
                    borderColor="#A1A0A0"
                    LabelClassName={"fw-3"}
                    placeholder={"Enter office address"}
                    name="office_address"
                    type="text"
                  />
                </div>
              </div>
              <div className="my-4 w-full text-center ">
                <small className="text-[#858585] text-base font-semibold ">
                  A mail will be sent to the information for confirmation
                </small>
              </div>
              <div className="d-flex justify-content-center  pb-4">
                <div className="col-4">
                  <ButtonComp
                    btnText={isLoading ? "Adding..." : "Submit information"}
                    btnClassName={
                      "w-100 bg-primary1 py-[12px] text-white border-0 rounded-[0.625rem] fw-3"
                    }
                    loading={isLoading}
                    disabled={isLoading}
                  />
                </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </section>
  );
}

